import { useOne, useForm, useList, useGo } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import {
  Loader2,
  Info,
  Pencil,
  DollarSign,
  Users,
  Ticket,
  ShieldCheck,
  Hash,
  Users2,
  BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { FormHeader } from '@/components/forms';
import {
  EntityAnalyticsCard,
  EntityAnalyticsGrid,
  TabsWithPersistence,
} from '@/components/shared';
import { PricingTiersSection } from '@/components/pricing-tiers';
import type { TranslationLanguage } from '@/components/translations';
import {
  type ChannelPass,
  type ChannelPassFormData,
  getChannelPassName,
  getLimitActionLabel,
} from './types';
import { ChannelPassesFormFields, buildFormState } from './form';
import { ChannelPassAnalyticsTab } from './ChannelPassAnalyticsTab';
import { ChannelPassMemberships } from './ChannelPassMemberships';

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
};

type TelegramChannelRecord = {
  id: number;
  title: string;
  username: string | null;
};

type TriggerRecord = {
  id: number;
  name: string;
};

export function ChannelPassesShow() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();

  const { query: passQuery } = useOne<ChannelPass>({
    resource: 'channel-passes',
    id: Number(id),
  });

  const pass = passQuery?.data?.data;
  const isLoading = passQuery?.isLoading;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!pass) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Подписка не найдена</p>
        <Button variant="link" onClick={() => go({ to: '/channel-passes' })}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const name = getChannelPassName(pass);

  return (
    <div className="space-y-6">
      <FormHeader
        title={name}
        onBack={() => go({ to: '/channel-passes' })}
        titleAddon={
          pass.isActive ? (
            <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20">Активна</Badge>
          ) : (
            <Badge variant="secondary">Неактивна</Badge>
          )
        }
        icon={<Ticket className="h-5 w-5" />}
        sticky
      />

      <TabsWithPersistence
        paramName="tab"
        defaultTab="overview"
        tabs={[
          {
            value: 'overview',
            label: 'Обзор',
            icon: <Info className="h-4 w-4" />,
            content: <OverviewTab pass={pass} />,
          },
          {
            value: 'analytics',
            label: 'Аналитика',
            icon: <BarChart3 className="h-4 w-4" />,
            content: <ChannelPassAnalyticsTab passId={Number(id)} />,
          },
          {
            value: 'edit',
            label: 'Редактирование',
            icon: <Pencil className="h-4 w-4" />,
            content: <EditTab passId={Number(id)} pass={pass} />,
          },
          {
            value: 'pricing',
            label: 'Тарифы',
            icon: <DollarSign className="h-4 w-4" />,
            content: <PricingTab passId={Number(id)} />,
          },
        ]}
        className="space-y-4"
      />
    </div>
  );
}

// --- Overview Tab ---

function OverviewTab({ pass }: { pass: ChannelPass }) {
  const fillPercent = pass.userLimit
    ? Math.min(Math.round((pass.currentUserCount / pass.userLimit) * 100), 100)
    : null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) {
      return '—';
    }
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Карточки-метрики */}
      <EntityAnalyticsGrid>
        <EntityAnalyticsCard
          title="Текущие пользователи"
          value={pass.currentUserCount}
          icon={<Users className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Лимит"
          value={pass.userLimit ?? '\u221E'}
          icon={<Hash className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Заполненность"
          value={fillPercent !== null ? `${fillPercent}%` : '—'}
          icon={<ShieldCheck className="h-4 w-4" />}
          description={
            fillPercent !== null
              ? `${pass.currentUserCount} из ${pass.userLimit}`
              : 'Без ограничений'
          }
        />
        <EntityAnalyticsCard
          title="Статус"
          value={pass.isActive ? 'Активна' : 'Неактивна'}
          icon={<Ticket className="h-4 w-4" />}
        />
      </EntityAnalyticsGrid>

      {/* Основная информация */}
      <Card>
        <CardHeader>
          <CardTitle>Основная информация</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <InfoRow label="Канал">
              {pass.channel ? (
                <span>
                  {pass.channel.title}
                  {pass.channel.username && (
                    <span className="text-muted-foreground ml-1">
                      (@{pass.channel.username})
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </InfoRow>
            <InfoRow label="Тарифов">
              {pass.pricingTiers?.length ?? 0}
            </InfoRow>
            <InfoRow label="Тип лимита">
              {getLimitActionLabel(pass.onLimitAction)}
            </InfoRow>
          </dl>
        </CardContent>
      </Card>

      {/* Ограничения */}
      <Card>
        <CardHeader>
          <CardTitle>Ограничения</CardTitle>
        </CardHeader>
        <CardContent>
          {!pass.hasLimits ? (
            <p className="text-muted-foreground text-sm">Ограничения не установлены</p>
          ) : (
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <InfoRow label="Лимит пользователей">
                {pass.userLimit ?? 'Не задан'}
              </InfoRow>
              <InfoRow label="Продажи до">
                {formatDate(pass.saleEndDate)}
              </InfoRow>
              <InfoRow label="Действие при лимите">
                {getLimitActionLabel(pass.onLimitAction)}
              </InfoRow>
              <InfoRow label="Задержка (дней)">
                {pass.onLimitDelayDays}
              </InfoRow>
            </dl>
          )}

          {/* Progress bar для заполненности */}
          {pass.userLimit && (
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Заполненность</span>
                <span className="font-medium">
                  {pass.currentUserCount} / {pass.userLimit} ({fillPercent}%)
                </span>
              </div>
              <Progress value={fillPercent ?? 0} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Участники канала */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users2 className="h-4 w-4" />
            Участники канала
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChannelPassMemberships channelId={pass.channelId} />
        </CardContent>
      </Card>
    </div>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-0.5 font-medium">{children}</dd>
    </div>
  );
}

// --- Edit Tab ---

function EditTab({ passId, pass }: { passId: number; pass: ChannelPass }) {
  const { formLoading, onFinish, query } = useForm<ChannelPass>({
    resource: 'channel-passes',
    action: 'edit',
    id: passId,
    redirect: false,
  });

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
  });

  const { result: channelsResult } = useList<TelegramChannelRecord>({
    resource: 'telegram-channels',
    pagination: { pageSize: 100 },
  });

  const { result: triggersResult } = useList<TriggerRecord>({
    resource: 'triggers',
    pagination: { pageSize: 100 },
  });

  const languages: TranslationLanguage[] | undefined = languagesResult?.data?.map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName,
    flag: l.flag || l.flagEmoji || '\uD83C\uDFF3\uFE0F',
  }));

  const channels = channelsResult?.data ?? [];
  const triggers = triggersResult?.data ?? [];

  // Используем данные из query формы (они могут быть свежее)
  const formPass = query?.data?.data ?? pass;
  const initialState = buildFormState(formPass);
  const formKey = `edit-tab-${formPass.id}-${formPass.updatedAt}`;

  return (
    <ChannelPassesFormFields
      key={formKey}
      isEdit
      formLoading={formLoading}
      initialState={initialState}
      channels={channels}
      triggers={triggers}
      onFinish={async (v: ChannelPassFormData) => {
        await onFinish(v);
      }}
      onBack={() => {/* noop — embedded mode */}}
      languages={languages}
      embedded
    />
  );
}

// --- Pricing Tab ---

function PricingTab({ passId }: { passId: number }) {
  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
  });

  const languages: TranslationLanguage[] | undefined = languagesResult?.data?.map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName,
    flag: l.flag || l.flagEmoji || '\uD83C\uDFF3\uFE0F',
  }));

  return (
    <PricingTiersSection
      productKey={{ channelPassId: passId }}
      languages={languages}
    />
  );
}
